import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import { LoadableStatus } from '../mobx-utils';
import { useStore } from '../core/store';
import { useTranslation } from '../core/utils/translations';
import {
  Button,
  TeaserServiceLarge as ServiceTeaserLarge,
  TeaserServiceSmall as ServiceTeaserSmall,
} from '@cfm-media/pwa-ui-components';

import { ContactInformationForm } from '../components/contact-information-form';
import { ContentWrapper } from '../components/content-wrapper';
import { HeroDashboard } from '../components/hero-dashboard';
import { InstallNotification } from '../components/install-notification';
import { Loader } from '../components/loader';
import { PageWrapper } from '../components/page-wrapper';
import { SpecialCampaignModal } from '../components/special-campaign-modal';
import { getUniqueCountries } from '../utils/passolution';
import { isLimousineServiceActive } from './limousine-service';
import { urls } from '../urls';
import { useBookingCountdown } from '../hooks/useBookingCountdown';
import { useBookingInformation } from '../hooks/useBookingInformation';
import { useBookingTeasers } from '../hooks/useBookingTeasers';
import { useContactInformationForm } from '../hooks/useContactInformationForm';
import {
  bookingTeaserDisplayConfig,
  extrasTeaserConfig,
  extrasTeaserDisplayConfig,
  serviceTeaserConfig,
  serviceTeaserDisplayConfig,
} from '../utils/teasers';

export const Home = observer(() => {
  const { t } = useTranslation([
    'weather-page',
    'home-page',
    'people-and-places',
    'airport-parking',
  ]);
  const navigate = useNavigate();
  const { BookingStore } = useStore();
  const bookingTeasers = useBookingTeasers();
  const bookingInformation = useBookingInformation();
  const countdownConfig = useBookingCountdown();
  const { showForm } = useContactInformationForm();

  if (BookingStore?.booking?.status !== LoadableStatus.done) {
    return <Loader />;
  }

  let dynamicServiceTeasers = serviceTeaserConfig;

  if (
    BookingStore?.booking?.data?.flights.length &&
    !extrasTeaserConfig.find((teaser) => teaser.url === urls.airportParking)
  ) {
    extrasTeaserConfig.push({
      url: urls.airportParking,
      headline: t('airport-parking:teaser-headline'),
      iconClass: 'icon-parking',
      image: '/public/assets/images/teasers/airport-parking.webp',
    });
  }

  let isTurkeyBooking =
    BookingStore?.booking?.data?.serviceName?.charAt(1) === 'T';

  if (BookingStore.validGeoDataFromHotels.length) {
    dynamicServiceTeasers.weather = {
      description: t('weather-page:teaser-description'),
      onClick: navigate.bind(null, urls.weather),
      headline: t('weather-page:teaser-headline'),
      iconClass: 'icon-weather',
      image: '/public/assets/images/teasers/weather.jpg',
    };
  }

  if (isTurkeyBooking) {
    dynamicServiceTeasers.peopleAndPlaces = {
      description: t('people-and-places:teaser-description'),
      onClick: navigate.bind(null, urls.peopleAndPlaces),
      headline: t('people-and-places:teaser-headline'),
      iconClass: 'icon-magazines',
      image: '/public/assets/images/teasers/land-und-leute.png',
    };
  }

  if (
    getUniqueCountries(
      BookingStore?.booking?.data?.hotels,
      BookingStore?.booking?.data?.flights,
    ).length
  ) {
    dynamicServiceTeasers.entryConditions = {
      description: 'Alle Infos zu den Einreisebestimmungen Ihres Reiselands',
      onClick: navigate.bind(null, urls.entryConditions),
      headline: 'Einreiseinformationen',
      iconClass: 'icon-passport',
      image: '/public/assets/images/teasers/passolution.jpg',
    };
  }

  if (
    isLimousineServiceActive(BookingStore) &&
    !extrasTeaserConfig.find((teaser) => teaser.url === urls.limousineService)
  ) {
    extrasTeaserConfig.push({
      headline: 'Home2Gate - Airporttransfer',
      iconClass: 'icon-chauffeur',
      image: '/public/assets/images/teasers/limousine-service.jpg',
      url: urls.limousineService,
    });
  }

  return (
    <PageWrapper>
      <HeroDashboard
        intro="Viel Freude bei Ihrer Reise" // TODO: use a CMS key
        {...bookingInformation}
        {...countdownConfig}
        overlay
      />

      <ContentWrapper>
        <div className="mb-3 text-xl text-brand-1 font-semibold uppercase text-left ">
          {t('home-page:my-trip-headline')}
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          {bookingTeasers?.map((bookingTeaser) => (
            <>
              <ServiceTeaserSmall
                onClick={() => {
                  navigate(bookingTeaser.url);
                  window.tap.event('teaser-click', {
                    pty: 'teaser',
                    pid: bookingTeaser.headline,
                  });
                }}
                {...bookingTeaser}
                {...bookingTeaserDisplayConfig}
                key={bookingTeaser.headline}
              />
            </>
          ))}
        </div>

        <div className="mb-3 text-xl text-brand-1 font-semibold uppercase">
          {t('home-page:extras-headline')}
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          {extrasTeaserConfig.map((extrasTeaser) => (
            <ServiceTeaserLarge
              onClick={() => {
                navigate(extrasTeaser.url);
                window.tap.event('teaser-click', {
                  pty: 'teaser',
                  pid: extrasTeaser.headline,
                });
              }}
              {...extrasTeaserDisplayConfig}
              {...extrasTeaser}
              key={extrasTeaser.headline}
              button={
                <Button textSize="text-sm" title="weiter" variant="primary" />
              }
            />
          ))}
        </div>

        <div className="mb-3 text-xl text-brand-1 font-semibold uppercase">
          {t('home-page:services-headline')}
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          {Object.keys(dynamicServiceTeasers).map((key) => (
            <ServiceTeaserLarge
              onClick={() => {
                navigate(dynamicServiceTeasers[key].url);

                window.tap.event('teaser-click', {
                  pty: 'teaser',
                  pid: key,
                });
              }}
              {...serviceTeaserDisplayConfig}
              {...dynamicServiceTeasers[key]}
              key={key}
              button={
                <Button textSize="text-sm" title="weiter" variant="primary" />
              }
            />
          ))}
        </div>
        <InstallNotification />
        <SpecialCampaignModal />

        {showForm ? <ContactInformationForm /> : null}
      </ContentWrapper>
    </PageWrapper>
  );
});
